"use client";

import { useEffect, useRef } from "react";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { TOUR_MIN_VIEWPORT_WIDTH, type TourId } from "../lib/registry";
import { useToursStore } from "../lib/tour-store";

const AUTO_START_DELAY_MS = 1000;

/**
 * Auto-starts a one-time tour when the hosting page is ready:
 *  - first ever visit (not seen yet), or
 *  - an explicit replay request (e.g. from Getting Started).
 *
 * Pass `ready` = data loaded / landmarks rendered. Never starts while another
 * tour is active, and only auto-starts once per mount.
 */
export function useAutoStartTour(tourId: TourId, ready: boolean = true) {
  const hydrated = useToursStore((state) => state.hydrated);
  const seen = useToursStore((state) => !!state.seen[tourId]);
  const activeTourId = useToursStore((state) => state.activeTourId);
  const requested = useToursStore(
    (state) => state.requestedTourId === tourId,
  );
  const setAreaTour = useToursStore((state) => state.setAreaTour);
  const startedRef = useRef(false);

  // Register this page's tour so the navbar "replay tour" button knows what to
  // re-run. Cleared on unmount unless another screen already took over.
  useEffect(() => {
    if (!FEATURE_FLAGS.tours) return;
    setAreaTour(tourId);
    return () => {
      if (useToursStore.getState().areaTourId === tourId) setAreaTour(null);
    };
  }, [tourId, setAreaTour]);

  useEffect(() => {
    if (!FEATURE_FLAGS.tours) return;
    if (!ready || !hydrated || activeTourId) return;
    if (!requested && (seen || startedRef.current)) return;
    if (window.innerWidth < TOUR_MIN_VIEWPORT_WIDTH) return;

    startedRef.current = true;
    const timer = setTimeout(() => {
      const store = useToursStore.getState();
      if (store.activeTourId) return;
      if (store.requestedTourId === tourId) store.clearRequest();
      store.start(tourId);
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [ready, hydrated, seen, activeTourId, requested, tourId]);
}
